import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Link as LinkIcon } from "lucide-react";
import { authHeader } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolve = (url) => (url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

const ImageCard = ({ img, onUpdated }) => {
  const [url, setUrl] = useState(img.url);
  const [busy, setBusy] = useState(false);

  const saveUrl = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/images/${img.key}`, { url }, { headers: authHeader() });
      toast.success(`${img.label} updated.`);
      onUpdated();
    } catch {
      toast.error("Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/images/${img.key}/upload`, fd, { headers: authHeader() });
      setUrl(res.data.url);
      toast.success(`${img.label} — image uploaded.`);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="bg-white border border-[#E3DED4]" data-testid={`image-card-${img.key}`}>
      <div className="aspect-[16/9] overflow-hidden bg-[#EBE7DF]">
        <img src={resolve(img.url)} alt={img.label} className="w-full h-full object-cover" />
      </div>
      <div className="p-4">
        <p className="text-sm font-semibold text-[#121926]">{img.label}</p>
        <p className="font-mono text-[10px] text-[#8A94A2] mt-0.5">{img.key}</p>
        <div className="mt-3 flex gap-2">
          <input
            data-testid={`image-url-input-${img.key}`}
            className="flex-1 border border-[#E3DED4] px-3 py-2 text-xs focus:outline-none focus:border-[#B85D2A]"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
          />
          <button onClick={saveUrl} disabled={busy} data-testid={`image-url-save-${img.key}`} className="px-3 py-2 bg-[#1A2533] text-white hover:bg-[#B85D2A] transition-colors disabled:opacity-60" title="Save URL">
            <LinkIcon size={13} />
          </button>
          <label className="px-3 py-2 bg-[#B85D2A] text-white hover:bg-[#1A2533] transition-colors cursor-pointer" title="Upload file">
            <Upload size={13} />
            <input data-testid={`image-upload-${img.key}`} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadFile} disabled={busy} />
          </label>
        </div>
      </div>
    </div>
  );
};

export const ImagesManager = () => {
  const [images, setImages] = useState([]);

  const load = () => axios.get(`${API}/images`).then((r) => setImages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div>
      <h2 className="font-serif text-xl text-[#121926] mb-1">Site Images</h2>
      <p className="text-xs text-[#718096] mb-6">Paste an image URL or upload a file (JPEG/PNG/WEBP, max 8MB). Changes are live immediately.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {images.map((img) => <ImageCard key={img.key} img={img} onUpdated={load} />)}
      </div>
    </div>
  );
};
